import { AuthUser } from "@/modules/auth/entities/AuthUser";
import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from "@reduxjs/toolkit";
import type { RootState } from "@/store";
import {
  FetchCurrentUserAsync,
  LogoutAsync,
  SignUpAsync,
  RefreshTokenAsync,
} from "../infrastructure/auth-service";
import {
  validateOtpAsync,
  resendOtpAsync,
} from "../infrastructure/otp-service";
import { LoginAsync } from "../login/services/login-service";
import { AuthErrorPayload } from "../entities/AuthError";

interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  isLoading: boolean;
  error: string | null;
  loginTimestamp: number | null;
  roles: string[];
  permissions: string[];
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  isLoading: false,
  error: null,
  loginTimestamp: null,
  roles: [],
  permissions: [],
};

export const signupWithCredentials = createAsyncThunk(
  "auth/signupWithCredentials",
  async (
    credentials: { userName: string; password: string; acceptedTermsVersion?: string; acceptedPrivacyVersion?: string; captchaToken?: string; },
    { rejectWithValue },
  ) => {
    try {
      const response = await SignUpAsync(credentials);
      return { otp: response.otp, message: response.message };
    } catch (error) {
      const errorMessage =
        (error as Record<string, Record<string, Record<string, string>>>)?.response?.data?.message || (error as Error).message || "Signup failed";
      return rejectWithValue(errorMessage);
    }
  },
);

export const loginWithCredentials = createAsyncThunk(
  "auth/loginWithCredentials",
  async (
    credentials: { email: string; password: string; inviteToken?: string; captchaToken?: string },
    { rejectWithValue },
  ) => {
    try {
      const data = await LoginAsync(credentials)

      const authUser: AuthUser = {
        user: {
          id: data.user.id,
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          email: data.user.email,
          status: data.user.status,
          createdAt: data.user.createdAt,
          lastLoginAt: data.user.lastLoginAt,
          needsLegalConsent: data.user.needsLegalConsent,
          organizationRole: data.user.organizationRole,
        },
        roles: data.roles,
        permissions: data.permissions,
        currentOrganizationId: data.user.currentOrganizationId || null,
      }

      return { user: authUser }
    } catch (err: unknown) {
      const authErr = err as AuthErrorPayload
      return rejectWithValue({
        error: authErr.error ?? "Login failed",
        errorCode: authErr.errorCode ?? null,
      })
    }
  },
)

export const logoutCurrentUser = createAsyncThunk(
  "auth/logoutCurrentUser",
  async () => {
    try {
      await LogoutAsync();
      return true;
    } catch (_error) {
      return true;
    }
  },
);

export const fetchUserProfile = createAsyncThunk(
  "auth/fetchUserProfile",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };

      if (!state.auth.isAuthenticated) {
        throw new Error("Not authenticated");
      }

      const data = await FetchCurrentUserAsync();

      const authUser: AuthUser = {
        user: {
          id: data.id,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          status: data.status,
          createdAt: data.createdAt,
          lastLoginAt: data.lastLoginAt,
          needsLegalConsent: data.needsLegalConsent,
          organizationRole: data.organizationRole,
        },
        roles: data.roles,
        permissions: data.permissions,
        currentOrganizationId: data.currentOrganizationId,
        organizations: data.organizations,
      };

      return authUser;
    } catch (error) {
      const errorMessage =
        (error as Record<string, Record<string, Record<string, string>>>)?.response?.data?.message ||
        (error as Error).message ||
        "Failed to fetch profile";
      return rejectWithValue(errorMessage);
    }
  },
);

export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (_, { rejectWithValue }) => {
    try {
      const data = await RefreshTokenAsync();

      const authUser: AuthUser = {
        user: {
          id: data.user.id,
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          email: data.user.email,
          status: data.user.status,
          createdAt: data.user.createdAt,
          lastLoginAt: data.user.lastLoginAt,
          needsLegalConsent: data.user.needsLegalConsent,
          organizationRole: data.user.organizationRole,
        },
        roles: data.roles,
        permissions: data.permissions,
        currentOrganizationId: data.user.currentOrganizationId || null,
      };

      return { user: authUser };
    } catch (error) {
      const errorMessage =
        (error as Record<string, Record<string, Record<string, string>>>)?.response?.data?.message ||
        (error as Error).message ||
        "Token refresh failed";
      return rejectWithValue(errorMessage);
    }
  },
);

export const validateOtp = createAsyncThunk(
  "auth/validateOtp",
  async (credentials: { email: string; otp: string; captchaToken?: string }, { rejectWithValue }) => {
    try {
      const data = await validateOtpAsync(credentials);

      const authUser: AuthUser = {
        user: {
          id: data.user.id,
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          email: data.user.email,
          status: data.user.status,
          createdAt: data.user.createdAt,
          lastLoginAt: data.user.lastLoginAt,
          needsLegalConsent: data.user.needsLegalConsent,
          organizationRole: data.user.organizationRole,
        },
        roles: data.roles,
        permissions: data.permissions,
        currentOrganizationId: data.user.currentOrganizationId || null,
      };

      return { user: authUser };
    } catch (error) {
      const errorMessage =
        (error as Record<string, Record<string, Record<string, string>>>)?.response?.data?.message ||
        (error as Error).message ||
        "OTP validation failed";
      return rejectWithValue(errorMessage);
    }
  },
);

export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async (payload: { email: string; captchaToken?: string } | string, { rejectWithValue }) => {
    try {
      const args = typeof payload === "string" ? { email: payload } : payload;
      const response = await resendOtpAsync(args);
      return response;
    } catch (error) {
      const errorMessage =
        (error as Record<string, Record<string, Record<string, string>>>)?.response?.data?.message ||
        (error as Error).message ||
        "Failed to resend OTP";
      return rejectWithValue(errorMessage);
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.error = null;
      state.loginTimestamp = null;
      state.roles = [];
      state.permissions = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    restoreAuth: (
      state,
      action: PayloadAction<{
        user: AuthUser;
        loginTimestamp: number;
      }>,
    ) => {
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        loginTimestamp: action.payload.loginTimestamp,
        roles: action.payload.user?.roles ?? [],
        permissions: action.payload.user?.permissions ?? [],
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginWithCredentials.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithCredentials.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload?.user ?? null;
        state.roles = action.payload?.user?.roles ?? [];
        state.permissions = action.payload?.user?.permissions ?? [];
        state.loginTimestamp = Date.now();
        state.error = null;
      })
      .addCase(loginWithCredentials.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as string) || action.error?.message || "Login failed";
      })
      .addCase(logoutCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutCurrentUser.fulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = null;
        state.loginTimestamp = null;
        state.roles = [];
        state.permissions = [];
      })
      .addCase(logoutCurrentUser.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = null;
        state.loginTimestamp = null;
        state.roles = [];
        state.permissions = [];
      })
      .addCase(signupWithCredentials.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signupWithCredentials.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(signupWithCredentials.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as string) ||
          action.error?.message ||
          "Signup failed";
      })
      .addCase(fetchUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload as AuthUser;
        state.error = null;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as string) ||
          action.error?.message ||
          "Failed to fetch profile";
      })
      .addCase(refreshToken.pending, () => { })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = action.payload?.user ?? null;
        state.roles = action.payload?.user?.roles ?? [];
        state.permissions = action.payload?.user?.permissions ?? [];
        state.loginTimestamp = Date.now();
        state.error = null;
      })
      .addCase(refreshToken.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = null;
        state.loginTimestamp = null;
      })
      .addCase(validateOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(validateOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload?.user ?? null;
        state.roles = action.payload?.user?.roles ?? [];
        state.permissions = action.payload?.user?.permissions ?? [];
        state.loginTimestamp = Date.now();
        state.error = null;
      })
      .addCase(validateOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(resendOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resendOtp.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  logout,
  clearError,
  restoreAuth,
} = authSlice.actions;

export const selectUser = (state: RootState) => state.auth.user;
export const selectIsAuthenticated = (state: RootState) =>
  state.auth.isAuthenticated;
export const selectIsLoading = (state: RootState) => state.auth.isLoading;
export const selectError = (state: RootState) => state.auth.error;
export const selectRoles = (state: RootState) => state.auth.roles;
export const selectPermissions = (state: RootState) => state.auth.permissions;
export const selectAuthContextData = createSelector(
  [
    selectUser,
    selectIsAuthenticated,
    selectIsLoading,
    selectError,
    selectRoles,
    selectPermissions,
  ],
  (
    user,
    isAuthenticated,
    isLoading,
    error,
    roles,
    permissions,
  ) => ({
    user,
    isAuthenticated,
    isLoading,
    error,
    roles,
    permissions,
  }),
);

export default authSlice.reducer;
